import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { SECTORS, SECTOR_LIST } from "@/data/sectorConfig";
import { regioniProvince } from "@/data/regioniProvince";
import { trackLeadConversion } from "@/components/TrackingScripts";
import {
  ArrowRight, ArrowLeft, Check, Loader2, Phone, Sparkles, X,
  Home, Warehouse, Building2, Castle,
} from "lucide-react";
import { CheckboxLabel } from "@/components/CheckboxLabel";

const API = "/api";
const regionNames = Object.keys(regioniProvince).sort();

const ROOF_IMAGES = {
  tegole: "https://static.prod-images.emergentagent.com/jobs/95ef2a0f-2128-46f3-98b8-27eca1970d4c/images/0b57f2093117b485da60a1067b0dc5c0ea8a1d901040282a81a7866144e1aae8.png",
  coppi: "https://static.prod-images.emergentagent.com/jobs/95ef2a0f-2128-46f3-98b8-27eca1970d4c/images/2b05de22eea4b36e3fa5ea718d4f1a1f740f8fb43e8bc4d8dd11e4d12a346d0b.png",
  piano: "https://static.prod-images.emergentagent.com/jobs/95ef2a0f-2128-46f3-98b8-27eca1970d4c/images/ce51f76d288322a26bfe677005a8df20dfd35718062d5191e33383c3eb564cc9.png",
  lamiera: "https://static.prod-images.emergentagent.com/jobs/95ef2a0f-2128-46f3-98b8-27eca1970d4c/images/2c2f463497991278c8629e47b0269972de2964b493b9002793fb92cda6b1e1e4.png",
};

const propertyTypes = [
  { id: "casa_indipendente", label: "Casa Indipendente", desc: "Villa o casa singola", icon: Home, color: "emerald" },
  { id: "villetta", label: "Villetta a Schiera", desc: "Villetta bifamiliare o a schiera", icon: Warehouse, color: "blue" },
  { id: "appartamento", label: "Appartamento", desc: "Appartamento in condominio", icon: Building2, color: "violet" },
  { id: "villa", label: "Villa", desc: "Villa con giardino", icon: Castle, color: "amber" },
];

const aptInstallOptions = [
  { id: "tetto", label: "Tetto di Proprieta", desc: "Ho accesso al tetto del condominio o un lastrico solare di proprieta", icon: "roof" },
  { id: "balcone", label: "Balcone", desc: "Impianto da balcone autoinstallante, facile e senza permessi", icon: "balcony" },
];

const roofTypes = [
  { id: "tegole", label: "Tegole", desc: "Tetto con tegole classiche in laterizio", image: ROOF_IMAGES.tegole },
  { id: "coppi", label: "Coppi", desc: "Tetto con coppi tradizionali curvi", image: ROOF_IMAGES.coppi },
  { id: "piano", label: "Piano / Terrazza", desc: "Tetto piano o terrazza praticabile", image: ROOF_IMAGES.piano },
  { id: "lamiera", label: "Lamiera", desc: "Tetto in lamiera o pannelli metallici", image: ROOF_IMAGES.lamiera },
];

const consumptionRanges = [
  { id: "50", label: "Fino a 50 \u20ac", kwh: "~1.500 kWh/anno", level: 1 },
  { id: "100", label: "50 \u20ac - 100 \u20ac", kwh: "~3.000 kWh/anno", level: 2 },
  { id: "150", label: "100 \u20ac - 150 \u20ac", kwh: "~4.500 kWh/anno", level: 3 },
  { id: "200", label: "150 \u20ac - 200 \u20ac", kwh: "~6.000 kWh/anno", level: 4 },
  { id: "300", label: "Oltre 200 \u20ac", kwh: "~8.000+ kWh/anno", level: 5 },
];

const sectorColorMap = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-500", text: "text-emerald-600", ring: "ring-emerald-500/20", badge: "bg-emerald-500" },
  blue: { bg: "bg-blue-50", border: "border-blue-500", text: "text-blue-600", ring: "ring-blue-500/20", badge: "bg-blue-500" },
  amber: { bg: "bg-amber-50", border: "border-amber-500", text: "text-amber-600", ring: "ring-amber-500/20", badge: "bg-amber-500" },
  violet: { bg: "bg-violet-50", border: "border-violet-500", text: "text-violet-600", ring: "ring-violet-500/20", badge: "bg-violet-500" },
};

const cardColorMap = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-500", text: "text-emerald-600", ring: "ring-emerald-500/20", badge: "bg-emerald-500", iconBg: "bg-emerald-100" },
  blue: { bg: "bg-blue-50", border: "border-blue-500", text: "text-blue-600", ring: "ring-blue-500/20", badge: "bg-blue-500", iconBg: "bg-blue-100" },
  violet: { bg: "bg-violet-50", border: "border-violet-500", text: "text-violet-600", ring: "ring-violet-500/20", badge: "bg-violet-500", iconBg: "bg-violet-100" },
  amber: { bg: "bg-amber-50", border: "border-amber-500", text: "text-amber-600", ring: "ring-amber-500/20", badge: "bg-amber-500", iconBg: "bg-amber-100" },
};

function getSteps(selectedSector, formData) {
  const steps = [{ id: "sector", title: "Seleziona il Settore" }];
  if (selectedSector === "residenziale") {
    steps.push({ id: "property_type", title: "Tipo di Abitazione" });
    if (formData.property_type === "appartamento") {
      steps.push({ id: "apt_install", title: "Tipo di Installazione" });
    }
    if (!(formData.property_type === "appartamento" && formData.apt_installation === "balcone")) {
      steps.push({ id: "roof_type", title: "Tipo di Tetto" });
    }
    steps.push({ id: "consumption", title: "Consumo Energetico" });
  } else if (selectedSector) {
    steps.push({ id: "details", title: "Dettagli Specifici" });
  }
  steps.push({ id: "location", title: "Dove Ti Trovi?" });
  steps.push({ id: "personal", title: "I Tuoi Dati" });
  return steps;
}

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i < current ? "bg-emerald-500 w-6" : i === current ? "bg-emerald-400 w-8" : "bg-slate-200 w-4"}`} />
    ))}
  </div>
);

export const SectorWizard = ({ onClose, initialSector, initialFormData }) => {
  const prefilledData = initialFormData || {};
  const skipToConsumption = prefilledData.property_type === "appartamento" && prefilledData.apt_installation === "balcone";

  const resolveInitialStep = () => {
    if (!initialSector) return 0;
    if (skipToConsumption) {
      const tempSteps = getSteps(initialSector, prefilledData);
      const idx = tempSteps.findIndex((s) => s.id === "consumption");
      return idx >= 0 ? idx : 1;
    }
    return 1;
  };

  const [stepIdx, setStepIdx] = useState(resolveInitialStep());
  const [selectedSector, setSelectedSector] = useState(initialSector || "");
  const [formData, setFormData] = useState(prefilledData);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [privacyText, setPrivacyText] = useState("");
  const [privacyLink, setPrivacyLink] = useState({ text: "", url: "" });
  const [extraCheckboxes, setExtraCheckboxes] = useState([]);
  const [extraChecked, setExtraChecked] = useState({});

  useEffect(() => {
    axios.get(`${API}/privacy-text`).then((r) => {
      setPrivacyText(r.data.text || "");
      setPrivacyLink({ text: r.data.link_text || "", url: r.data.link_url || "" });
      setExtraCheckboxes(r.data.checkboxes || []);
    }).catch(() => {});
  }, []);

  const sector = selectedSector ? SECTORS[selectedSector] : null;
  const colors = sector ? sectorColorMap[sector.color] : null;
  const steps = useMemo(() => getSteps(selectedSector, formData), [selectedSector, formData]);
  const currentStep = steps[stepIdx];
  const totalSteps = steps.length;
  const availableProvinces = formData.region ? (regioniProvince[formData.region] || []) : [];

  const technicalFields = sector && selectedSector !== "residenziale"
    ? sector.formFields.filter((f) => !f.otp && !["name", "surname", "email", "phone"].includes(f.key))
    : [];

  const set = (key, val) => setFormData((prev) => ({ ...prev, [key]: val }));

  const canProceed = () => {
    if (!currentStep) return false;
    switch (currentStep.id) {
      case "sector": return selectedSector !== "";
      case "property_type": return !!formData.property_type;
      case "apt_install": return !!formData.apt_installation;
      case "roof_type": return !!formData.roof_type;
      case "consumption": return !!formData.monthly_bill;
      case "details": {
        const req = technicalFields.filter((f) => f.required);
        return req.every((f) => formData[f.key] && formData[f.key] !== "");
      }
      case "location": return !!formData.region && !!formData.province;
      case "personal": {
        const baseOk = !!formData.name && !!formData.email && !!formData.phone && phoneVerified && !!formData.privacy_accepted;
        const requiredOk = extraCheckboxes.filter(cb => cb.required).every((_, i) => {
          const idx = extraCheckboxes.findIndex((c, ci) => c.required && extraCheckboxes.slice(0, ci + 1).filter(x => x.required).length === i + 1);
          return extraChecked[idx];
        });
        const allRequiredChecked = extraCheckboxes.every((cb, i) => !cb.required || extraChecked[i]);
        return baseOk && allRequiredChecked;
      }
      default: return false;
    }
  };

  const goNext = () => {
    const newSteps = getSteps(selectedSector, formData);
    const currentId = steps[stepIdx]?.id;
    const nextIdx = newSteps.findIndex((s) => s.id === currentId) + 1;
    if (nextIdx < newSteps.length) setStepIdx(nextIdx);
  };

  const goBack = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const handleSendOTP = async () => {
    if (!formData.phone || formData.phone.length < 10) { toast.error("Inserisci un numero di telefono valido"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/otp/send`, { phone: formData.phone });
      setOtpSent(true);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nell'invio OTP"); }
    finally { setLoading(false); }
  };

  const handleVerifyOTP = async (code) => {
    setOtpCode(code);
    if (code.length === 6) {
      setLoading(true);
      try {
        await axios.post(`${API}/otp/verify`, { phone: formData.phone, code });
        setPhoneVerified(true);
        toast.success("Numero verificato con successo!");
      } catch { toast.error("Codice non valido"); setOtpCode(""); }
      finally { setLoading(false); }
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = { ...formData, sector: selectedSector, privacy_accepted: true };
      if (selectedSector === "residenziale") {
        payload.monthly_consumption = parseInt(formData.monthly_bill) || 0;
        if (formData.property_type === "appartamento" && formData.apt_installation === "balcone") {
          payload.installation_type = "balcone_autoinstallante";
        }
      }
      await axios.post(`${API}/leads`, payload);
      trackLeadConversion({ source: "wizard", name: payload.name, email: payload.email });
      setSubmitted(true);
      toast.success("Richiesta inviata con successo!");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nell'invio"); }
    finally { setLoading(false); }
  };

  const isLastStep = stepIdx === totalSteps - 1;

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="wizard-success">
        <div className="bg-white rounded-3xl p-8 sm:p-12 max-w-md w-full text-center shadow-2xl animate-fade-in-up">
          <div className={`w-20 h-20 rounded-full ${colors?.bg || "bg-emerald-100"} flex items-center justify-center mx-auto mb-6 animate-count-up`}>
            <Check className={`w-10 h-10 ${colors?.text || "text-emerald-600"}`} strokeWidth={2.5} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>Richiesta Inviata!</h2>
          <p className="text-slate-500 mb-8 text-sm leading-relaxed">
            {formData.apt_installation === "balcone"
              ? "Ti contatteremo per proporti le migliori soluzioni fotovoltaiche da balcone autoinstallanti."
              : `I migliori partner della tua zona ti contatteranno presto con preventivi personalizzati per il settore ${sector?.label || ""}.`}
          </p>
          <button onClick={onClose} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-8 py-3 btn-scale" data-testid="wizard-success-close">Chiudi</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="sector-wizard-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl animate-fade-in-up overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-6 sm:px-8 pt-6 pb-4 bg-gradient-to-b from-slate-50 to-white">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${colors?.text || "text-emerald-600"}`}>Passo {stepIdx + 1} di {totalSteps}</div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="wizard-title">{currentStep?.title}</h2>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors" data-testid="wizard-close"><X className="w-5 h-5" /></button>
          </div>
          <StepIndicator current={stepIdx} total={totalSteps} />
        </div>

        {/* Content */}
        <div className="px-6 sm:px-8 flex-1 overflow-y-auto py-5">

          {/* SECTOR SELECTION */}
          {currentStep?.id === "sector" && (
            <div className="animate-fade-in" data-testid="wizard-step-sector">
              <p className="text-sm text-slate-500 mb-5">Per quale settore desideri un preventivo fotovoltaico?</p>
              <div className="grid grid-cols-2 gap-3">
                {SECTOR_LIST.map((s) => {
                  const sc = sectorColorMap[s.color];
                  const selected = selectedSector === s.id;
                  const Icon = s.icon;
                  return (
                    <button key={s.id} onClick={() => { setSelectedSector(s.id); setStepIdx(0); }}
                      className={`relative border-2 rounded-2xl p-4 text-left transition-all duration-200 active:scale-[0.97] ${selected ? `${sc.border} ${sc.bg} ring-4 ${sc.ring}` : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid={`wizard-sector-${s.id}`}>
                      <div className={`w-11 h-11 rounded-xl ${selected ? sc.bg : "bg-slate-50"} flex items-center justify-center mb-3 transition-colors`}>
                        <Icon className={`w-5 h-5 ${selected ? sc.text : "text-slate-400"}`} strokeWidth={1.5} />
                      </div>
                      <div className="font-semibold text-sm text-slate-900">{s.label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{s.stats[0].value} {s.stats[0].label}</div>
                      {selected && <div className={`absolute top-3 right-3 w-5 h-5 rounded-full ${sc.badge} flex items-center justify-center`}><Check className="w-3 h-3 text-white" strokeWidth={3} /></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* PROPERTY TYPE (Residenziale) */}
          {currentStep?.id === "property_type" && (
            <div className="animate-fade-in" data-testid="wizard-step-property">
              <p className="text-sm text-slate-500 mb-5">Seleziona il tipo di immobile dove installare l'impianto.</p>
              <div className="grid grid-cols-2 gap-3">
                {propertyTypes.map((pt) => {
                  const c = cardColorMap[pt.color];
                  const selected = formData.property_type === pt.id;
                  return (
                    <button key={pt.id} onClick={() => set("property_type", pt.id)}
                      className={`relative border-2 rounded-2xl p-4 text-left transition-all duration-200 active:scale-[0.97] ${selected ? `${c.border} ${c.bg} ring-4 ${c.ring}` : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid={`wizard-property-${pt.id}`}>
                      <div className={`w-11 h-11 rounded-xl ${selected ? c.iconBg : "bg-slate-50"} flex items-center justify-center mb-3 transition-colors`}>
                        <pt.icon className={`w-5 h-5 ${selected ? c.text : "text-slate-400"}`} strokeWidth={1.5} />
                      </div>
                      <div className="font-semibold text-sm text-slate-900">{pt.label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{pt.desc}</div>
                      {selected && <div className={`absolute top-3 right-3 w-5 h-5 rounded-full ${c.badge} flex items-center justify-center`}><Check className="w-3 h-3 text-white" strokeWidth={3} /></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* APARTMENT INSTALLATION TYPE */}
          {currentStep?.id === "apt_install" && (
            <div className="animate-fade-in" data-testid="wizard-step-apt">
              <p className="text-sm text-slate-500 mb-5">Hai un tetto di proprieta o un balcone disponibile?</p>
              <div className="flex flex-col gap-3">
                {aptInstallOptions.map((opt) => {
                  const selected = formData.apt_installation === opt.id;
                  return (
                    <button key={opt.id} onClick={() => set("apt_installation", opt.id)}
                      className={`relative border-2 rounded-2xl p-5 text-left transition-all duration-200 active:scale-[0.98] flex items-start gap-4 ${selected ? "border-emerald-500 bg-emerald-50 ring-4 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid={`wizard-apt-${opt.id}`}>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${selected ? "bg-emerald-100" : "bg-slate-50"}`}>
                        {opt.icon === "roof" ? (
                          <svg className={`w-6 h-6 ${selected ? "text-emerald-600" : "text-slate-400"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/></svg>
                        ) : (
                          <svg className={`w-6 h-6 ${selected ? "text-emerald-600" : "text-slate-400"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M4 14h16"/><path d="M4 14v4"/><path d="M20 14v4"/><circle cx="12" cy="9" r="2"/></svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-slate-900">{opt.label}</div>
                        <div className="text-xs text-slate-500 mt-1 leading-relaxed">{opt.desc}</div>
                        {opt.id === "balcone" && (
                          <span className="inline-block mt-2 text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Autoinstallante</span>
                        )}
                      </div>
                      {selected && <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-1"><Check className="w-3 h-3 text-white" strokeWidth={3} /></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ROOF TYPE */}
          {currentStep?.id === "roof_type" && (
            <div className="animate-fade-in" data-testid="wizard-step-roof">
              <p className="text-sm text-slate-500 mb-5">Che tipo di copertura ha il tuo tetto?</p>
              <div className="grid grid-cols-2 gap-3">
                {roofTypes.map((rt) => {
                  const selected = formData.roof_type === rt.id;
                  return (
                    <button key={rt.id} onClick={() => set("roof_type", rt.id)}
                      className={`relative border-2 rounded-2xl overflow-hidden text-left transition-all duration-200 active:scale-[0.97] ${selected ? "border-emerald-500 ring-4 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`wizard-roof-${rt.id}`}>
                      <div className="h-28 bg-slate-50 flex items-center justify-center overflow-hidden">
                        <img src={rt.image} alt={rt.label} className="w-full h-full object-contain p-2" loading="lazy" />
                      </div>
                      <div className={`p-3 ${selected ? "bg-emerald-50" : "bg-white"}`}>
                        <div className="font-semibold text-sm text-slate-900">{rt.label}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{rt.desc}</div>
                      </div>
                      {selected && <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"><Check className="w-3 h-3 text-white" strokeWidth={3} /></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* CONSUMPTION (Monthly Bill) */}
          {currentStep?.id === "consumption" && (
            <div className="animate-fade-in" data-testid="wizard-step-consumption">
              <p className="text-sm text-slate-500 mb-5">Quanto spendi mediamente al mese in bolletta elettrica?</p>
              <div className="flex flex-col gap-2.5">
                {consumptionRanges.map((cr) => {
                  const selected = formData.monthly_bill === cr.id;
                  return (
                    <button key={cr.id} onClick={() => set("monthly_bill", cr.id)}
                      className={`border-2 rounded-2xl p-4 text-left transition-all duration-200 active:scale-[0.98] flex items-center gap-4 ${selected ? "border-emerald-500 bg-emerald-50 ring-4 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid={`wizard-bill-${cr.id}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold ${selected ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>{cr.level}</div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-slate-900">{cr.label}</div>
                        <div className="text-xs text-slate-400">{cr.kwh}</div>
                      </div>
                      {selected && <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-white" strokeWidth={3} /></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* DETAILS (Other sectors) */}
          {currentStep?.id === "details" && sector && (
            <div className="animate-fade-in" data-testid="wizard-step-details">
              <p className="text-sm text-slate-500 mb-5">Aiutaci a capire meglio le tue esigenze per <strong>{sector.label}</strong>.</p>
              <div className="space-y-4">
                {(() => {
                  const rows = [];
                  let i = 0;
                  while (i < technicalFields.length) {
                    const f = technicalFields[i];
                    if (f.half && i + 1 < technicalFields.length && technicalFields[i + 1].half) {
                      rows.push({ type: "pair", fields: [f, technicalFields[i + 1]] });
                      i += 2;
                    } else { rows.push({ type: "single", field: f }); i += 1; }
                  }
                  return rows.map((row, ri) => {
                    if (row.type === "pair") {
                      return (
                        <div key={ri} className="grid grid-cols-2 gap-3">
                          {row.fields.map((f) => (
                            <div key={f.key}>
                              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{f.label} {f.required && <span className={colors.text}>*</span>}</label>
                              {f.type === "select" ? (
                                <select value={formData[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white input-focus-ring" data-testid={`wizard-field-${f.key}`}>
                                  <option value="">Seleziona...</option>
                                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                              ) : (
                                <Input type={f.type || "text"} value={formData[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="h-11 rounded-xl input-focus-ring" placeholder={f.label} data-testid={`wizard-field-${f.key}`} />
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    }
                    const f = row.field;
                    return (
                      <div key={f.key}>
                        <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{f.label} {f.required && <span className={colors.text}>*</span>}</label>
                        {f.type === "select" ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {f.options.map((opt) => {
                              const selected = formData[f.key] === opt;
                              return (
                                <button key={opt} onClick={() => set(f.key, opt)}
                                  className={`border-2 rounded-xl px-3 py-2.5 text-left text-sm transition-all duration-200 active:scale-[0.97] ${selected ? `${colors.border} ${colors.bg} ring-2 ${colors.ring} font-semibold` : "border-slate-200 hover:border-slate-300 bg-white text-slate-700"}`}
                                  data-testid={`wizard-opt-${f.key}-${opt.toLowerCase().replace(/\s+/g, "-")}`}>
                                  {selected && <Check className={`w-3 h-3 ${colors.text} inline mr-1`} strokeWidth={3} />}{opt}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <Input type={f.type || "text"} value={formData[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="h-11 rounded-xl input-focus-ring" placeholder={f.label} data-testid={`wizard-field-${f.key}`} />
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* LOCATION */}
          {currentStep?.id === "location" && (
            <div className="animate-fade-in" data-testid="wizard-step-location">
              <p className="text-sm text-slate-500 mb-5">Seleziona la tua zona per trovare i partner piu vicini a te.</p>
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-2 block">Regione</label>
                  <select value={formData.region || ""} onChange={(e) => { set("region", e.target.value); set("province", ""); }}
                    className="flex h-12 w-full rounded-xl border border-input bg-transparent px-3 py-1 text-base shadow-sm input-focus-ring" data-testid="wizard-region">
                    <option value="">Seleziona la regione</option>
                    {regionNames.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-2 block">Provincia</label>
                  <select value={formData.province || ""} onChange={(e) => set("province", e.target.value)} disabled={!formData.region}
                    className="flex h-12 w-full rounded-xl border border-input bg-transparent px-3 py-1 text-base shadow-sm input-focus-ring disabled:opacity-50 disabled:cursor-not-allowed" data-testid="wizard-province">
                    <option value="">{formData.region ? "Seleziona la provincia" : "Prima seleziona la regione"}</option>
                    {availableProvinces.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* PERSONAL DATA + OTP */}
          {currentStep?.id === "personal" && (
            <div className="animate-fade-in" data-testid="wizard-step-personal">
              <p className="text-sm text-slate-500 mb-5">Inserisci i tuoi dati per ricevere il preventivo personalizzato.</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nome <span className={colors?.text || "text-emerald-600"}>*</span></label>
                    <Input placeholder="Mario" value={formData.name || ""} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-xl input-focus-ring" data-testid="wizard-name" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Cognome</label>
                    <Input placeholder="Rossi" value={formData.surname || ""} onChange={(e) => set("surname", e.target.value)} className="h-11 rounded-xl input-focus-ring" data-testid="wizard-surname" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Email <span className={colors?.text || "text-emerald-600"}>*</span></label>
                  <Input type="email" placeholder="mario@email.com" value={formData.email || ""} onChange={(e) => set("email", e.target.value)} className="h-11 rounded-xl input-focus-ring" data-testid="wizard-email" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Telefono <span className={colors?.text || "text-emerald-600"}>*</span></label>
                  <div className="flex gap-2">
                    <Input type="tel" placeholder="+39 333 1234567" value={formData.phone || ""} onChange={(e) => { set("phone", e.target.value); setPhoneVerified(false); setOtpSent(false); }} className="h-11 rounded-xl input-focus-ring flex-1" disabled={phoneVerified} data-testid="wizard-phone" />
                    {!phoneVerified && (
                      <button onClick={handleSendOTP} disabled={loading || !formData.phone || formData.phone.length < 10}
                        className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl px-4 h-11 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors whitespace-nowrap" data-testid="wizard-send-otp">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                        {otpSent ? "Rinvia" : "Invia OTP"}
                      </button>
                    )}
                    {phoneVerified && <div className="flex items-center gap-1 text-emerald-600 font-semibold text-sm px-3" data-testid="wizard-phone-verified"><Check className="w-4 h-4" /> Verificato</div>}
                  </div>
                </div>
                {otpSent && !phoneVerified && (
                  <div className="animate-fade-in bg-slate-50 rounded-xl p-4" data-testid="wizard-otp-section">
                    <label className="text-sm font-semibold text-slate-700 mb-3 block text-center">Inserisci il codice OTP</label>
                    <div className="flex justify-center">
                      <InputOTP maxLength={6} value={otpCode} onChange={handleVerifyOTP} data-testid="wizard-otp-input">
                        <InputOTPGroup>{[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg font-bold rounded-lg border-slate-200" />)}</InputOTPGroup>
                      </InputOTP>
                    </div>
                    <p className="text-xs text-slate-400 text-center mt-2">Inserisci il codice a 6 cifre ricevuto via SMS</p>
                  </div>
                )}
                <label className="flex items-start gap-3 cursor-pointer mt-2">
                  <input type="checkbox" checked={formData.privacy_accepted || false} onChange={(e) => set("privacy_accepted", e.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" data-testid="wizard-privacy" />
                  <CheckboxLabel label={privacyText || "Acconsento al trattamento dei miei dati personali."} linkText={privacyLink.text} linkUrl={privacyLink.url} required />
                </label>
                {extraCheckboxes.map((cb, idx) => (
                  <label key={idx} className="flex items-start gap-3 cursor-pointer mt-1.5">
                    <input type="checkbox" checked={!!extraChecked[idx]} onChange={(e) => setExtraChecked(prev => ({ ...prev, [idx]: e.target.checked }))} className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" data-testid={`wizard-extra-checkbox-${idx}`} />
                    <CheckboxLabel label={cb.label} linkText={cb.link_text} linkUrl={cb.link_url} required={cb.required} />
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-slate-100 flex items-center justify-between bg-white">
          {stepIdx > 0 ? (
            <button onClick={goBack} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors rounded-full px-4 py-2 hover:bg-slate-50" data-testid="wizard-back">
              <ArrowLeft className="w-4 h-4" /> Indietro
            </button>
          ) : <div />}
          {!isLastStep ? (
            <button onClick={goNext} disabled={!canProceed()}
              className={`${colors?.badge || "bg-emerald-500"} hover:opacity-90 text-white font-bold rounded-full px-7 py-2.5 text-sm btn-scale shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2`} data-testid="wizard-next">
              Avanti <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={!canProceed() || loading}
              className={`${colors?.badge || "bg-emerald-500"} hover:opacity-90 text-white font-bold rounded-full px-7 py-2.5 text-sm btn-scale shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2`} data-testid="wizard-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Richiedi Preventivo
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
